import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useId, useState, type ReactElement } from "react";
import { useHeadingIsNarrow } from "_shared/_views/list-pages/list-heading-width";
import {
  availableChips,
  chooseListChip,
  effectiveListChipValues,
  hasListChips,
  listChipControl,
  listChipSummary,
  toggleListChip,
  toggleListChipShown,
  type ListChip,
  type ListChipCounts,
  type ListChipGroup,
  type ListChipSelection,
} from "_shared/_views/list-pages/list-chip-selection";

// #region Styled Components
const ChipRows = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  maxWidth: "100%",
  minWidth: 0,
  // On a phone the controls are one row, which scrolls sideways inside itself on the narrowest
  // screens rather than wrapping a control, or Clear, onto a row of its own.
  [theme.breakpoints.down("sm")]: {
    flexWrap: "nowrap",
    gap: theme.spacing(0.75),
    overflowX: "auto",
    scrollbarWidth: "none",
    width: "100%",
    "& > *": { flexShrink: 0 },
  },
}));
const ChipGroupRow = styled("div")({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  maxWidth: "100%",
  minWidth: 0,
});
const Count = styled("span")(({ theme }) => ({
  backgroundColor: theme.palette.action.selected,
  borderRadius: 9,
  fontSize: "0.75rem",
  fontWeight: 700,
  marginLeft: theme.spacing(0.75),
  minWidth: 18,
  padding: "0 6px",
  textAlign: "center",
}));
const Check = styled(CheckIcon)(({ theme }) => ({
  fontSize: 16,
  marginRight: theme.spacing(0.5),
  verticalAlign: "text-bottom",
}));
/**
 * A chip whose own icon sits as far from its leading edge as the count does from the trailing one.
 * MUI insets a chip's icon 5px, sized for an icon that draws inside its own box, while the label ends
 * 12px from the far edge; a swatch draws at its box's edge, so it is given the 12px.
 */
const ListChipButton = styled(Chip)({
  "& .MuiChip-icon": { marginRight: -4 },
  "&.MuiChip-filled .MuiChip-icon": { marginLeft: 12 },
  "&.MuiChip-outlined .MuiChip-icon": { marginLeft: 11 },
});
/** A menu's button, as tall and as round as a chip, so the row reads as one set of controls. */
const MenuButton = styled(Button)(({ theme }) => ({
  borderRadius: 16,
  height: 32,
  maxWidth: "100%",
  paddingLeft: theme.spacing(1.5),
  textTransform: "none",
  whiteSpace: "nowrap",
  "& .MuiButton-endIcon": { marginLeft: theme.spacing(0.25) },
  // The arrow takes the button's color as it turns from outlined to filled. MUI fades an icon's fill,
  // and Chromium left that fade at the outlined blue on the filled blue button, so the arrow vanished
  // once a choice was made from the menu (#6720).
  "& .MuiButton-endIcon .MuiSvgIcon-root": { transition: "none" },
}));
const MenuButtonText = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
});
const MenuGroupName = styled("span")(({ theme }) => ({
  color: "inherit",
  marginRight: theme.spacing(0.5),
  opacity: 0.8,
}));
const MenuChoice = styled(MenuItem)(({ theme }) => ({
  gap: theme.spacing(1),
  minWidth: 220,
  paddingLeft: theme.spacing(1),
  "& .MuiCheckbox-root": { padding: 0 },
}));
const MenuChoiceLabel = styled("span")({
  alignItems: "center",
  display: "flex",
  flex: "1 1 auto",
  gap: 8,
});
const Segments = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButton-root": {
    height: 32,
    paddingInline: theme.spacing(1.5),
    textTransform: "none",
    whiteSpace: "nowrap",
  },
}));
const ClearButton = styled(Chip)(({ theme }) => ({
  borderColor: "transparent",
  color: theme.palette.primary.main,
}));
const ClearIconButton = styled(IconButton)({ height: 32, width: 32 });
// #endregion

type ChipIcon = (parameter: string, value: string) => ReactElement | undefined;

type GroupProps = Readonly<{
  /** A phone's controls: each menu names its choice alone, and a one-value API's group is a menu too. */
  isCompact: boolean;
  group: ListChipGroup;
  chips: readonly ListChip[];
  groups: readonly ListChipGroup[];
  selection: ListChipSelection;
  onChange: (selection: ListChipSelection) => void;
  counts?: ListChipCounts;
  chipIcon?: ChipIcon;
}>;

/**
 * A list page's filters: one control per group of values, which widen inside (OR) and narrow across
 * (AND) (#6712).
 *
 * Each group is drawn as `listChipControl` says (#6720). A group of one value -- Archived -- is a chip
 * that toggles, and says so through `aria-pressed`. A group the API takes one value of is a segmented
 * control that starts with All. Any other group is a button that names the group and what it lets
 * through, "Category: Policies", and opens a menu of checkboxes. Nine chips in a row were hard to
 * scan and ran off a 1280px pane; one control per group fits beside the tabs.
 *
 * In a narrow heading -- on a phone, or beside a wide title row -- the controls are compact. A menu's
 * button names its choice alone, "Policies +1", or the group while nothing is chosen; a one-value API's
 * group is a menu of its values rather than segments; and Clear is a cross. On a phone they are one row.
 *
 * *Clear* appears once anything is chosen and returns the page's default list. Every value shows how
 * many rows it matches, once its count has arrived.
 *
 * A group whose values can match nothing given the others is not drawn: an archived proposal waits on
 * nobody, so the moves go while the archive alone is listed. A selection that equals a group's default
 * comes back as nothing chosen.
 */
export default function ListChips(
  props: Readonly<{
    groups: readonly ListChipGroup[];
    selection: ListChipSelection;
    onChange: (selection: ListChipSelection) => void;
    /** Each value's count, from the list's one counts request; a value with no count shows none. */
    counts?: ListChipCounts;
    /** A value's own icon, before its label: the Proposals moves lead with the row tint they list. */
    chipIcon?: ChipIcon;
    /** The parameters of groups the page draws elsewhere, as Documents draws its categories in a rail on a wide pane. */
    drawnElsewhere?: readonly string[];
  }>,
) {
  const { groups, selection, onChange, counts, chipIcon, drawnElsewhere = [] } = props;
  const isPhone = useMediaQuery(useTheme().breakpoints.down("sm"));
  const isCompact = useHeadingIsNarrow() || isPhone;
  const drawn = groups
    .filter((group) => !drawnElsewhere.includes(group.parameter))
    .map((group) => ({ group, chips: availableChips(group, selection) }))
    .filter(({ chips }) => chips.length > 0);
  if (drawn.length === 0 && !hasListChips(selection)) return null;

  return (
    <ChipRows>
      {drawn.map(({ group, chips }) => {
        const groupProps: GroupProps = { isCompact, group, chips, groups, selection, onChange, counts, chipIcon };
        const control = listChipControl(group);
        // A segmented control is a named group of its own.
        if (control === "segmented" && !isCompact) return <ChipSegments key={group.parameter} {...groupProps} />;
        return (
          <ChipGroupRow key={group.parameter} role="group" aria-label={group.label}>
            {control === "switch" ? <ChipSwitch {...groupProps} /> : <ChipMenu {...groupProps} />}
          </ChipGroupRow>
        );
      })}
      {hasListChips(selection) &&
        (isCompact ? (
          <ClearIconButton aria-label="Clear" color="primary" size="small" onClick={() => onChange({})}>
            <CloseIcon fontSize="small" />
          </ClearIconButton>
        ) : (
          <ClearButton label="Clear" variant="outlined" onClick={() => onChange({})} />
        ))}
    </ChipRows>
  );
}

function countOf(props: GroupProps, value: string): number | undefined {
  return props.counts?.[props.group.parameter]?.[value];
}

/** A one-value group: a chip that toggles, filled with a check while it is on. */
function ChipSwitch(props: GroupProps) {
  const { group, chips, groups, selection, onChange, chipIcon } = props;
  return chips.map((chip) => {
    const isOn = (selection[group.parameter] ?? []).includes(chip.value);
    const count = countOf(props, chip.value);
    return (
      <ListChipButton
        key={chip.value}
        icon={chipIcon?.(group.parameter, chip.value)}
        label={
          <>
            {isOn && <Check aria-hidden />}
            {chip.label}
            {count !== undefined && <Count>{count}</Count>}
          </>
        }
        color="primary"
        variant={isOn ? "filled" : "outlined"}
        aria-pressed={isOn}
        onClick={() => onChange(toggleListChip(groups, selection, group.parameter, chip.value))}
      />
    );
  });
}

const ALL = "";

/** A group the API takes one value of: All, then each value, one of them pressed. */
function ChipSegments(props: GroupProps) {
  const { group, chips, groups, selection, onChange } = props;
  const [chosen] = selection[group.parameter] ?? [];
  return (
    <Segments
      aria-label={group.label}
      exclusive
      size="small"
      color="primary"
      value={chosen ?? ALL}
      onChange={(_event, value: string | null) => {
        // Pressing the pressed segment again hands back null; the choice stays.
        if (value === null) return;
        onChange(chooseListChip(groups, selection, group.parameter, value === ALL ? undefined : value));
      }}
    >
      <ToggleButton value={ALL}>{group.allLabel ?? "All"}</ToggleButton>
      {chips.map((chip) => {
        const count = countOf(props, chip.value);
        return (
          <ToggleButton key={chip.value} value={chip.value}>
            {chip.label}
            {count !== undefined && <Count>{count}</Count>}
          </ToggleButton>
        );
      })}
    </Segments>
  );
}

/**
 * A group of several values that widen each other: a button naming the group and what it lets
 * through, and a menu of checkboxes. The menu ticks what the list shows, so the Proposals stages show
 * Drafts and Active ticked with nothing chosen, and stays open while the reader ticks.
 *
 * On a phone a group the API takes one value of is a menu too, of choices rather than ticks, and it
 * closes on a choice.
 */
function ChipMenu(props: GroupProps) {
  const { isCompact, group, chips, groups, selection, onChange, chipIcon } = props;
  const isExclusive = listChipControl(group) === "segmented";
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const chosen = selection[group.parameter] ?? [];
  const shown = effectiveListChipValues(group, selection) ?? [];
  const summary = listChipSummary(group, selection, isCompact);
  // A screen reader hears every choice, however short the button reads.
  const fullSummary = listChipSummary(group, selection);
  // The default of a group with a `sameAsNone` is a selection; its last tick is never taken away.
  const isLastDefaultTick = (value: string) => Boolean(group.sameAsNone) && shown.length === 1 && shown[0] === value;
  return (
    <>
      <MenuButton
        color="primary"
        variant={chosen.length > 0 ? "contained" : "outlined"}
        disableElevation
        endIcon={<ArrowDropDownIcon />}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-controls={anchor ? menuId : undefined}
        aria-label={`${group.label}: ${fullSummary}`}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        {isCompact ? (
          <MenuButtonText>{chosen.length > 0 ? summary : group.label}</MenuButtonText>
        ) : (
          <MenuButtonText>
            <MenuGroupName>{group.label}:</MenuGroupName>
            {summary}
          </MenuButtonText>
        )}
      </MenuButton>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        slotProps={{ list: { "aria-label": group.label } }}
      >
        <MenuChoice
          selected={chosen.length === 0}
          onClick={() => {
            onChange(chooseListChip(groups, selection, group.parameter, undefined));
            setAnchor(null);
          }}
        >
          <MenuChoiceLabel>{group.allLabel ?? "All"}</MenuChoiceLabel>
        </MenuChoice>
        <Divider />
        {chips.map((chip) => {
          const isTicked = shown.includes(chip.value);
          const count = countOf(props, chip.value);
          if (isExclusive) {
            return (
              <MenuChoice
                key={chip.value}
                role="menuitemradio"
                aria-checked={isTicked}
                selected={isTicked}
                onClick={() => {
                  onChange(chooseListChip(groups, selection, group.parameter, chip.value));
                  setAnchor(null);
                }}
              >
                <MenuChoiceLabel>{chip.label}</MenuChoiceLabel>
                {count !== undefined && <Count>{count}</Count>}
              </MenuChoice>
            );
          }
          return (
            <MenuChoice
              key={chip.value}
              role="menuitemcheckbox"
              aria-checked={isTicked}
              disabled={isTicked && isLastDefaultTick(chip.value)}
              onClick={() => onChange(toggleListChipShown(groups, selection, group.parameter, chip.value))}
            >
              <Checkbox checked={isTicked} size="small" tabIndex={-1} disableRipple slotProps={{ input: { "aria-hidden": true } }} />
              <MenuChoiceLabel>
                {chipIcon?.(group.parameter, chip.value)}
                {chip.label}
              </MenuChoiceLabel>
              {count !== undefined && <Count>{count}</Count>}
            </MenuChoice>
          );
        })}
      </Menu>
    </>
  );
}
